import React, { useEffect, useState, useRef } from 'react';
import { Button, DatePicker, Divider, Input, InputRef, Modal, Popconfirm, Select, Space, Spin, Table, Tooltip, notification } from 'antd';
import { ColumnType, ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { fetchAllClientEntities, fetchBuAndClientDetails_Opportunity, fetchSOWandClient, fetchSowDetails, getSowDetailsDownloadable, updateOpportunity } from '../../store/reducers/orderDashboardSlice';
import { checkAccess, getUserRole, isCRF_or_Amendment, rejectedApiMessage } from '../../utilities/CommonHelper';
import { useDispatch, useSelector } from 'react-redux';
import { getUserAccessData } from '../../utilities/sessionHelper';
import Link from 'antd/es/typography/Link';
import { FilterConfirmProps } from 'antd/es/table/interface';
import { SearchOutlined, InfoCircleTwoTone, InfoCircleOutlined, WarningOutlined, MailOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import ChangeOrdersModal from '../ChangeOrdersModal/changeOrdersModal';
import SOWAuditLog from '../SOWAuditLog/sowAuditLog';
import SowActionModal from '../SowActionModal/sowActionModal';
import TaggedSOWDetailsModal from '../TaggedSOWDetailsModal/taggedSOWDetailsModal';
import FloatLabel from '../../components/FloatLabel/floatLabel';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

interface DataType {
  key: React.Key;
  sowId: string;
  sowLink: string;
  clientEntity: string;
  sowName: string;
  sowOwner: string;
  bu: string;
  contractValue: string;
  sowStartDate: Date;
  sowEndDate: Date;
  salesSpoc: string;
  contractType: string;
  opportunityName: string;
  opportunityId: string;
  tagType: string;
  totalTcv: any;
  childVal: any;
  businessOwner: string;
  deliveryLead: string;
  serviceLine: string;
  sowBu: string | null;
  currency: string;
  contractValueUSD: string;
  woValue: string;
  woValueInUSD: string;
  pendingWoValueInUSD: string;
}

type DataIndex = keyof DataType;

const SowDetailsContainer = (props: any) => {
  const dispatch = useDispatch();
  const orderDashboardData = useSelector((state: any) => state.orderDashboard);
  const [sowDetailsData, setSowDetailsData] = useState<any>([]);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const role = getUserRole(getUserAccessData()?.roleAccessMap) || "";
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);
  const writeAccess_opportunityMapping: boolean = checkAccess(role, "Opportunity Mapping", getUserAccessData()?.roleAccessMap[role], "write");
  const writeAccess_order: boolean = checkAccess(role, "Order Dashboard", getUserAccessData()?.roleAccessMap[role], "write");
  const [selectedSOWData, setSelectedSOWData] = useState<any>({});
  const [changeOrderModalVisible, setChangeOrderModalVisible] = useState(false);
  const [viewAuditlogModal, setViewAuditlogModal] = useState(false);
  const [sowActionModalVisible, setSowActionModalVisible] = useState(false);
  const [showTaggedSOWDetailsModal, setShowTaggedSOWDetailsModal] = useState(false);
  const [tagType, setTagType] = useState('');
  const [filters, setFilters] = useState<any>([]);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [clientList, setClientList] = useState<any>([]);
  const [selectedClient, setSelectedClient] = useState<any>([]);
  const [reportDateRange, setReportDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const today = dayjs().endOf('day');
  const currentQuarterStart = dayjs().month(Math.floor(dayjs().month() / 3) * 3).startOf('month');
  const oldestAllowedDate = currentQuarterStart.subtract(21, 'month').startOf('day');

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex,
  ) => {
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    confirm();
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex=='childVal'? 'Is Child?': dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              setSelectedKeys([]);
              handleSearch([], confirm, dataIndex);
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex] && record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleHistory = (record: any) => {
    setSelectedSOWData(record);
    setChangeOrderModalVisible(true);
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'SOW ID',
      width: 150,
      dataIndex: 'sowId',
      key: 'sowId',
      fixed: 'left',
      ...getColumnSearchProps('sowId'),
      render: (text: string, record: any, index: number) => (
        <>
          <a className='maplink' href={record.contractLink} target='_blank' rel="noopener noreferrer">{text}</a>
          &nbsp; &nbsp;
          {record.child && isCRF_or_Amendment(record.contractType) && (
            <Tooltip title={<span>The Contract type of the SOW is CRF/Amendment in Spot Draft and it is also marked as Child. Please correct the data in Spot Draft.</span>}>
              <WarningOutlined style={{ color: '#b2a10a' }} />
            </Tooltip>
          )}
          {record.isTotalCurrentValueGreater && (
            <Tooltip title={<span>The Contract Value of the Master SOW is less than the Sum of Contract Value of all Child SOWs tagged to it. Please update the Contract Value of the SOWs in Spot Draft to ensure seamless Operations.</span>}>
              <WarningOutlined style={{ color: '#b2a10a' }} />
            </Tooltip>
          )}
        </>
      )
    },
    {
      title: 'Tag',
      dataIndex: 'tagType',
      key: 'tagType',
      width: 150,
      ...getColumnSearchProps('tagType'),
    },
    {
      title: 'Client Entity',
      width: 150,
      dataIndex: 'clientEntity',
      key: 'clientEntity',
      ...getColumnSearchProps('clientEntity'),
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 150,
      ...getColumnSearchProps('currency'),
    },
    {
      title: 'Sow Name',
      dataIndex: 'sowName',
      key: 'sowName',
      width: 150,
      ...getColumnSearchProps('sowName'),
    },
    {
      title: 'Total Contract Value (USD)',
      dataIndex: 'contractValueUSD',
      key: 'contractValueUSD',
      width: 200,
      ...getColumnSearchProps('contractValueUSD'),
      render: (text: string, record: any) => (
        <>
          {record.sowTagDetails == null && <span>{text}</span>}
          {record.sowTagDetails !== null && record.sowTagDetails.length > 0 && (
            <>
              <span>{text}</span> &nbsp;
              <Tooltip
                overlayInnerStyle={{
                  width: '800px',
                  background: 'white',
                  color: 'black',
                  borderRadius: '0',
                  borderTop: '4px solid #1677ff',
                  font: 'normal normal normal 14px/20px Nunito Sans',
                }}
                title={
                  <>
                    <span> Original Master SOW Contract value (LCY): {record.contractValue}</span><br />
                    <span>Original Master SOW Contact Value (USD): {record.originalContractValueUsd}</span><br />
                    <span> Updated Master SOW Contract Value (in USD): {record.contractValueUSD}</span><br />
                    <Divider orientation='left' style={{
                      color: 'black',
                      font: 'normal normal normal 14px/18px Nunito Sans',
                    }}><span>Tagged SOW Details</span></Divider>
                    <Table
                      columns={[
                        { title: 'SOW ID', dataIndex: 'sowId', key: '_sowId' },
                        { title: 'Tag Type', dataIndex: 'tagType', key: '_tagType' },
                        { title: 'Currency', dataIndex: 'currencyLcy', key: '_currencyLcy' },
                        { title: 'Contract Value (LCY)', dataIndex: 'contractValueLcy', key: '_contractValueLcy' },
                        { title: 'Contract Value (USD)', dataIndex: 'contractValueUsd', key: '_contractValueUsd' }
                      ]}
                      dataSource={record.sowTagDetails}
                      pagination={false}
                    />
                  </>
                }
              ><InfoCircleTwoTone /></Tooltip>
            </>
          )}
        </>
      )
    },
    {
      title: ' Total Contract Value (LCY)',
      dataIndex: 'totalTcv',
      key: 'totalTcv',
      width: 200,
      ...getColumnSearchProps('totalTcv'),
      render: (text: string, record: any) => (
        <>
          {record.sowTagDetails == null && (
            <>
              <span>{text}</span> &nbsp;
              {record.contractValue && record.contractValue !== record.totalTcv && (
                <Tooltip overlayInnerStyle={{ width: '600px' }} title={
                  <>
                    <span> Master Contract value: {record.contractValue}</span><br />
                    <span>CRF's/Amendment Contract value : {record.updatedTcv}</span><br />
                    <span>Updated TCV : {record.totalTcv}</span><br />
                    <span>Note : To check the details for the tagged CRF/Amendment, please click on the view Tagged SOW's from action.</span><br />
                  </>
                }><InfoCircleTwoTone /></Tooltip>
              )}
            </>
          )}
          {record.sowTagDetails !== null && record.sowTagDetails.length > 0 && (
            <span>The CRF/Amendment tagged to the Master SOW is of different Currency. The Contract Values has been converted to USD. Please check the Total Contract Value(USD)</span>
          )}
        </>
      )
    },
    {
      title: 'WO Value (LCY)',
      dataIndex: 'woValue',
      key: 'woValue',
      width: 150,
      ...getColumnSearchProps('woValue'),
    },
    {
      title: 'WO Value (USD)',
      dataIndex: 'woValueInUSD',
      key: 'woValueInUSD',
      width: 150,
      ...getColumnSearchProps('woValueInUSD'),
    },
    {
      title: 'Pending WO Value (USD)',
      dataIndex: 'pendingWoValueInUSD',
      key: 'pendingWoValueInUSD',
      width: 200,
      ...getColumnSearchProps('pendingWoValueInUSD'),
    },
    {
      title: 'Contract Type',
      dataIndex: 'contractType',
      key: 'contractType',
      width: 150,
      ...getColumnSearchProps('contractType'),
    },
    {
      title: 'Business Unit',
      dataIndex: 'sowBu',
      key: 'sowBu',
      width: 150,
      ...getColumnSearchProps('sowBu'),
    },
    {
      title: <Tooltip title={
        <>
          <p>
            This data is coming from Spotdraft. If the SOW is a Child SOW, then it should be tagged to the Master SOW.
          </p>
        </>
      }>
        <span style={{ display: 'inline' }}>Is Child? <InfoCircleOutlined style={{ color: '#034EA2' }} /></span>
      </Tooltip>,
      dataIndex: 'childVal',
      key: 'childVal',
      width: 150,
      ...getColumnSearchProps('childVal'),
    },
    {
      title: 'Sales SPOC',
      dataIndex: 'salesSpoc',
      key: 'salesSpoc',
      width: 150,
      ...getColumnSearchProps('salesSpoc'),
    },
    {
      title: 'Business Owner',
      dataIndex: 'businessOwner',
      key: 'businessOwner',
      width: 200,
      ...getColumnSearchProps('businessOwner'),
    },
    {
      title: 'Service Line',
      dataIndex: 'serviceLine',
      key: 'serviceLine',
      width: 150,
      ...getColumnSearchProps('serviceLine'),
    },
    {
      title: 'Delivery Lead',
      dataIndex: 'deliveryLead',
      key: 'deliveryLead',
      width: 150,
      ...getColumnSearchProps('deliveryLead'),
    },
    {
      title: 'Opportunity ID',
      fixed: 'right',
      dataIndex: 'opportunityId',
      width: 180,
      key: 'opportunityId',
      ...getColumnSearchProps('opportunityId'),
      render: (text: string, record: any) => (
        <>
          {text ?
            <>
              <span>{text}</span>
              <br />
              <Link className='maplink' onClick={() => handleOpportunityClick(record, "view")}>View</Link>
              &nbsp; &nbsp;
              <Tooltip title={!writeAccess_opportunityMapping ? "Only Accessible by the Role : Sales" : isCRF_or_Amendment(record.contractType) ? "This SOW is a CRF/Amendment" : record.child ? "This SOW is a Child SOW. Please tag it to the Master SOW." : ""}>
                <Link className='maplink' disabled={!writeAccess_opportunityMapping || isCRF_or_Amendment(record.contractType) || record.child} onClick={() => handleOpportunityClick(record, "relink")}>Relink</Link>
              </Tooltip>
              &nbsp; &nbsp;
              <Tooltip title={!writeAccess_opportunityMapping ? "Only Accessible by the Role : Sales" : isCRF_or_Amendment(record.contractType) ? "This SOW is a CRF/Amendment" : record.child ? "This SOW is a Child SOW. Please tag it to the Master SOW." : ""} >
                <Popconfirm title="Are you sure to delink this opportunity?" onConfirm={() => handleUnMapOpportunity(record)} disabled={!writeAccess_opportunityMapping} okText="Yes" cancelText="No">
                  <Link className='maplink' disabled={!writeAccess_opportunityMapping || isCRF_or_Amendment(record.contractType) || record.child} onClick={() => null}>Delink</Link>
                </Popconfirm>
              </Tooltip>
            </>
            :
            <Tooltip title={!writeAccess_opportunityMapping ? "Only Accessible by the Role : Sales" : isCRF_or_Amendment(record.contractType) ? "This SOW is a CRF/Amendment" : record.child ? "This SOW is a Child SOW. Please tag it to the Master SOW." : ""}>
              <Link className='maplink' disabled={!writeAccess_opportunityMapping || isCRF_or_Amendment(record.contractType) || record.child} onClick={() => handleOpportunityClick(record, "link")}>Link</Link>
            </Tooltip>
          }
        </>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'bu',
      key: 'workOrders',
      width: 150,
      fixed: 'right',
      render: (text: string, record: any) => (
        <>
          <Tooltip title={!writeAccess_order && "Only Accessible by the Role : Finance"}>
            <Link className='maplink' disabled={!writeAccess_order} onClick={() => { setSelectedSOWData(record); setSowActionModalVisible(true) }}>Tag SOW</Link>
          </Tooltip>
          &nbsp; 
          {record.isCrfAmdSow && (
            <Tooltip title={<span>The Contract Type of the SOW/SOWs has been changed in Spot Draft. Please correct the data in Spot Draft so that it aligns with myOrders to ensure Seamless Operations.</span>}>
              <WarningOutlined style={{ color: '#b2a10a' }} />
            </Tooltip>
          )}
          <br />
          {record.tagType === "MASTER" && 
          <><a className='maplink' onClick={() => showchildSOWs(record, true, "MASTER")}>view Tagged SOWs</a>
          &nbsp;
          {record.isCrfAmdSow && (
            <Tooltip title={<span>The Contract Type of the SOW/SOWs has been changed in Spot Draft. Please correct the data in Spot Draft so that it aligns with myOrders to ensure Seamless Operations.</span>}>
              <WarningOutlined style={{ color: '#b2a10a' }} />
            </Tooltip>
          )}
          <br /></>
          }
          {record.duplicateCheck && <><a className='maplink' onClick={() => showchildSOWs(record, true, "DUPLICATE")}>view Duplicate SOWs</a><br /></>}
          <a className='maplink' onClick={() => props.showWorkOrderTab(record, true)}>View Work Orders</a>
          <br />
          <a className='maplink' onClick={() => handleHistory(record)}>View Change Order</a>
          <br />
          <a className='maplink' onClick={() => { setSelectedSOWData(record); setViewAuditlogModal(true) }}>View History</a>
        </>
      )
    }
  ];

  const handleOpportunityClick = (record: any, action: string) => {
    props.showMapModal(record, action);
  };

  const showchildSOWs = (record: any, isVisible: boolean, tagType: any) => {
    setSelectedSOWData(record);
    setShowTaggedSOWDetailsModal(isVisible);
    setTagType(tagType);
  };

  const handleUnMapOpportunity = (record: any) => {
    dispatch(updateOpportunity({ 'sowId': record.sowId, 'opportunityName': "", action: 'delink' })).then((res: any) => {
      if (res.type === 'orderDashboard/updateOpportunity/fulfilled') {
        notification.success({ message: 'Success', description: 'Opportunity delinked successfully' });
        fetchSowDetailsWithPagination(filters);
      } else {
        rejectedApiMessage(res.payload);
      }
    });
  };

  const handleTableChange = (pagination: TablePaginationConfig, filters: any) => {
    const { current, pageSize } = pagination;
    const _current = current ? current - 1 : 0;
    setPageNo(_current);
    setPageSize(pageSize || 10);
    const _filters: any[] = [];
    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key] !== undefined) {
        if (key === "tagType" || key === "totalTcv" || key === "childVal") {
          _filters.push({ field: key, value: filters[key][0].toUpperCase(), operator: 'EQUALS' });
        } else {
          _filters.push({ field: key, value: filters[key][0], operator: 'LIKE' });
        }
      }
    });
    setFilters(_filters);
    dispatch(fetchSowDetails({ 'page': _current, 'size': pageSize, 'filters': _filters })).then((res: any) => {
      if (res.type !== 'orderDashboard/fetchSowDetails/fulfilled') {
        rejectedApiMessage(res.payload);
      } else {
        setSowDetailsData(res.payload?.content);
        setTotalCount(res.payload?.totalElements);
        setPageNo(_current);
      }
    });
  };

  const fetchSowDetailsWithPagination = (newFilters: any) => {
    dispatch(fetchSowDetails({ 'page': pageNo, 'size': pageSize, 'filters': newFilters })).then((res: any) => {
      if (res.type !== 'orderDashboard/fetchSowDetails/fulfilled') {
        rejectedApiMessage(res.payload);
      } else {
        setSowDetailsData(res.payload?.content);
        setTotalCount(res.payload?.totalElements);
        setPageNo(pageNo);
      }
    });
  };

  const handleTableRefresh = (refreshTable: boolean) => {
    if (refreshTable) {
      fetchSowDetailsWithPagination(filters);
    }
  };

  useEffect(() => {
    if (props.activeTab === '1') {
      props.showWorkOrderTab({}, false);
      fetchSowDetailsWithPagination(filters);
      dispatch(fetchAllClientEntities()).then((response: any) => {
        if (response.type !== 'orderDashboard/fetchAllClientEntities/fulfilled') {
          rejectedApiMessage(response.payload);
        } else {
          setClientList(Array.isArray(response.payload) ? response.payload : []);
        }
      });
    }
  }, [orderDashboardData.isSOWDataUpdated, props.activeTab]);

  useEffect(() => {
    setSowDetailsData(Array.isArray(orderDashboardData.sowDetails) ? orderDashboardData.sowDetails : []);
    setPageNo(0);
  }, [orderDashboardData.sowDetails]);

  useEffect(() => {
    if (!(orderDashboardData.opportunity_BuList?.length >= 0 && orderDashboardData.opportunity_clientEntityList?.length >= 0)) {
      dispatch(fetchBuAndClientDetails_Opportunity()).then((res: any) => {
        if (res.type !== 'orderDashboard/fetchBuAndClientDetails_Opportunity/fulfilled') {
          rejectedApiMessage(res.payload);
        }
      });
    }
  }, []);

  const handleReportDownload = () => {
    const startDate = reportDateRange?.[0];
    const endDate = reportDateRange?.[1];

    if (!startDate || !endDate) {
      notification.error({
        message: 'Date Range Required',
        description: 'Please select a valid date range to generate the report.',
      });
      return;
    }

    if (startDate.isAfter(today, 'day') || endDate.isAfter(today, 'day')) {
      notification.error({
        message: 'Invalid Date Range',
        description: 'Future dates are not allowed.',
      });
      return;
    }

    if (startDate.isBefore(oldestAllowedDate, 'day') || endDate.isBefore(oldestAllowedDate, 'day')) {
      notification.error({
        message: 'Invalid Date Range',
        description: 'Please select dates only from the last 8 quarters.',
      });
      return;
    }

    if (!endDate.isAfter(startDate, 'day')) {
      notification.error({
        message: 'Invalid Date Range',
        description: 'End date should be greater than start date.',
      });
      return;
    }

    if (endDate.isAfter(startDate.add(24, 'month').endOf('day'))) {
      notification.error({
        message: 'Invalid Date Range',
        description: 'Please select a date range up to 8 quarters only.',
      });
      return;
    }

    dispatch(getSowDetailsDownloadable({ 'payload': { 'clientEntity': selectedClient, 'startDate': startDate.format('YYYY-MM-DD'), 'endDate': endDate.format('YYYY-MM-DD') } })).then(
      (response: any) => {
        if (response.type !== 'orderDashboard/getSowDetailsDownloadable/fulfilled') {
          rejectedApiMessage(response.payload);
        } else {

          notification.success({
                            message: 'Success',
                            description: 'The Data for the Selected Client Entities will be mailed to you',
                          });

          // download functionality has been disabled as per the new requirement 
          
          // const fileName = 'SOW_Details.xlsx';
          // const blob = response.payload?.data;
          // const url = window.URL.createObjectURL(blob);
          // const link = document.createElement('a');
          // link.href = url;
          // link.setAttribute('download', fileName);
          // document.body.appendChild(link);
          // link.click();
          // link.remove();
          setOpenDownloadModal(!openDownloadModal);
          setSelectedClient([]);
          setReportDateRange(null);
        }
      }
    );
  };

  const handleChangedValue = (field: string, value: any) => {
    if (field === 'client') {
      setSelectedClient(value);
    }
  };

  const getclientlistdata_selectedclient_List_atstartingpositions_without_duplication = (clientList: any, selectedClient: any) => {
    let tempClientList = [...clientList];
    let tempSelectedClient = [...selectedClient];
    let tempClientList1 = tempClientList.filter((item: any) => !tempSelectedClient.includes(item));
    const getSortKey = (item: any) => typeof item === 'string' ? item : (item.label || item.value || '');
    tempClientList1.sort((a: any, b: any) => getSortKey(a).localeCompare(getSortKey(b)));
    return [...tempSelectedClient, ...tempClientList1];
  };

  const handleSelectAll = () => {
    setSelectedClient(clientList);
  };

  const isReportDateRangeInvalid = () => {
    const startDate = reportDateRange?.[0];
    const endDate = reportDateRange?.[1];

    if (!startDate || !endDate) {
      return true;
    }

    if (startDate.isAfter(today, 'day') || endDate.isAfter(today, 'day')) {
      return true;
    }

    if (startDate.isBefore(oldestAllowedDate, 'day') || endDate.isBefore(oldestAllowedDate, 'day')) {
      return true;
    }

    if (!endDate.isAfter(startDate, 'day')) {
      return true;
    }

    return endDate.isAfter(startDate.add(24, 'month').endOf('day'));
  };

  const isGenerateReportDisabled = selectedClient.length === 0 || isReportDateRangeInvalid();

  return (
    <>
      <div className='containerr'>
        <div className='row'>
          <h2 className='col-lg-6 col-sm-12 page-heading'>
            SOW DASHBOARD
          </h2>
          <div className='col-lg-6 col-sm-12' style={{ display: 'flex', justifyContent: 'flex-end', padding: '20px' }}>
            <Button type="primary" style={{ borderRadius: '0', backgroundColor: '#034ea2', font: 'normal normal 600 12px / 16px Nunito Sans' }}
              size={'large'}
              onClick={() => setOpenDownloadModal(!openDownloadModal)}>
              Generate Report
            </Button>
          </div>
        </div>
        <Table
          className='table-style'
          columns={columns}
          loading={orderDashboardData.loading}
          dataSource={sowDetailsData}
          scroll={{ x: 'max-content', y: 350 }}
          pagination={{
            current: pageNo + 1,
            pageSize,
            total: totalCount,
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
        />
      </div>
      <ChangeOrdersModal visible={changeOrderModalVisible} onCancel={() => {
        setChangeOrderModalVisible(false);
        setSelectedSOWData({});
      }} selectedSOWData={selectedSOWData} />

      <SOWAuditLog visible={viewAuditlogModal} onCancel={() => { setViewAuditlogModal(false); setSelectedSOWData({}) }} selectedSOW={selectedSOWData} />
      <SowActionModal visible={sowActionModalVisible} onCancel={() => { setSowActionModalVisible(false); setSelectedSOWData({}) }} selectedSOW={selectedSOWData} onSuccess={() => {
        setSowActionModalVisible(false);
        setSelectedSOWData({});
        fetchSowDetailsWithPagination(filters)
      }} />

      <TaggedSOWDetailsModal visible={showTaggedSOWDetailsModal} onCancel={() => { showchildSOWs({}, false, "") }} tagType={tagType} selectedSOW={selectedSOWData} handleRefresh={handleTableRefresh} />

      <Modal
        title={<div>
          <span style={{ font: 'normal normal bold 14px / 19px Nunito Sans' }}>Download SOW Details
          </span>
        </div>}
        open={openDownloadModal}
        onCancel={() => { setOpenDownloadModal(!openDownloadModal); setSelectedClient([]); setReportDateRange(null); }}
        footer={null}
        maskClosable={false}
        width={500}
        styles={{
          content: {
            borderRadius: '0',
            borderTop: '4px solid #1677ff',
            font: 'normal normal normal 14px/20px Nunito Sans',
            zIndex: 9998,
          },
          body: {
            paddingTop: '18px',
          },
        }}
      >
        <Spin spinning={orderDashboardData.loading} >
          <div className='custom-fieldcolumn' style={{ margin: '0 10px 12px 10px' }}>
            <FloatLabel label="Client Entity *"
              value={selectedClient}
              isMultiSelect={true}
            >
              <Select
                autoClearSearchValue
                mode="multiple"
                size='middle'
                className='financialspoc-select'
                style={{ width: '100%', maxHeight: '100px', minHeight: '58px' }}
                optionFilterProp="children"
                value={selectedClient.map((item: any) => ({ value: item, label: item }))}
                maxTagCount={'responsive'}
                onChange={(value: string) => handleChangedValue('client', value)}
                filterOption={(input: any, option: any) =>
                  option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                options={getclientlistdata_selectedclient_List_atstartingpositions_without_duplication(clientList, selectedClient).map((item: any) => ({ value: item, label: item }))}
                dropdownRender={menu => (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-around', margin: '5px' }}>
                      <Link onClick={() => handleSelectAll()} disabled={selectedClient.length === clientList.length} >Select All</Link>
                      <Link onClick={() => handleChangedValue('client', [])} disabled={selectedClient.length == 0}>Clear All</Link>
                    </div>
                    {menu}
                  </>
                )}
              >
              </Select>
            </FloatLabel>
          </div>
          <div className='custom-fieldcolumn' style={{ margin: '0 10px 12px 10px' }}>
            <div style={{ font: 'normal normal 700 14px / 18px Nunito Sans', marginBottom: '6px' }}>Date Range *</div>
            <RangePicker
              style={{ width: '100%' }}
              value={reportDateRange}
              format={'YYYY-MM-DD'}
              allowClear
              onChange={(dates) => setReportDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
              disabledDate={(current) => !!current && (current > today || current < oldestAllowedDate)}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'end', margin: '6px 10px 0 10px' }}>
            <Button
              type="primary"
              style={{ borderRadius: '0', backgroundColor: isGenerateReportDisabled ? undefined : '#034ea2', font: 'normal normal 600 12px / 16px Nunito Sans' }}
              icon={<MailOutlined />}
              size={'large'}
              disabled={isGenerateReportDisabled}
              onClick={() => handleReportDownload()}
            >
              Export &amp; Send to Email
            </Button>
          </div>
        </Spin>
      </Modal>
    </>
  )
}
export default SowDetailsContainer;